import {
  Checkbox as ReactAriaCheckbox,
  type CheckboxProps as ReactAriaCheckboxProps,
  composeRenderProps,
} from "react-aria-components";

import { FormFieldAssistiveText } from "#components/v2/shared/FormFieldAssistiveText";
import { useUIVersion } from "#hooks";
import { CheckboxIcon } from "#icons";
import { customTv } from "#utils";

import { boxStyles } from "./Checkbox.styles";

const checkboxStyles = customTv({
  // `relative` is required so the visually-hidden <input> rendered by
  // react-aria-components (which uses `position: absolute`) is positioned
  // relative to this label rather than the document root. Without it,
  // clicking the checkbox can scroll the page out of view when the
  // checkbox is nested inside a scrollable container (e.g. a table).
  base: "relative flex items-center gap-2 text-label-medium transition",
  variants: {
    isDisabled: {
      true: "text-text-disabled cursor-not-allowed",
    },
    isInvalid: {
      true: "",
    },
    uiVersion: {
      v1: "",
      v2: "",
    },
  },
});

export interface CheckboxProps extends ReactAriaCheckboxProps {
  className?: string;
  assistiveText?: string;
}

export function Checkbox({
  className,
  assistiveText,
  isInvalid,
  isDisabled,
  ...props
}: CheckboxProps) {
  const uiVersion = useUIVersion();

  return (
    <div className="flex flex-col gap-y-[.15rem]">
      <ReactAriaCheckbox
        className={composeRenderProps(className, (className, renderProps) =>
          checkboxStyles({ ...renderProps, className, uiVersion, isInvalid }),
        )}
        {...props}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
      >
        {({ isSelected, isIndeterminate, ...renderProps }) => (
          <>
            <div
              className={boxStyles({
                isSelected: isSelected || isIndeterminate,
                uiVersion,
                ...renderProps,
              })}
            >
              {isIndeterminate ? (
                <span className="text-sm leading-none flex items-center justify-center font-bold text-neutral-white">
                  -
                </span>
              ) : isSelected ? (
                <CheckboxIcon className="text-neutral-white" size={8} />
              ) : null}
            </div>
            {props.children}
          </>
        )}
      </ReactAriaCheckbox>

      <FormFieldAssistiveText isDisabled={isDisabled} isInvalid={isInvalid}>
        {assistiveText}
      </FormFieldAssistiveText>
    </div>
  );
}
